"use client"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Progress } from "@/components/ui/progress"
import {
    Lock,
    Search,
    Clock,
    Coins,
    ExternalLink,
    Copy,
    CheckCircle2,
    AlertTriangle,
    Timer,
    Wallet,
    Calendar,
    TrendingUp,
    Shield,
    Droplets,
} from "lucide-react"
import Link from "next/link"
import { useState, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { useWallet } from "@/lib/wallet-context"
import { useTokenLocker, useTokenApproval, ERC20_ABI } from "@/lib/contract-hooks"
import { useReadContract, useChainId, useSwitchChain, useReadContracts } from "wagmi"
import { formatUnits, parseUnits } from "viem"
import { toast } from "sonner"
import { ChevronDown, RefreshCcw, Sparkles } from "lucide-react"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"

// Countdown Hook
function useCountdown(targetDate: Date) {
    const [timeLeft, setTimeLeft] = useState({
        days: 0,
        hours: 0,
        minutes: 0,
        seconds: 0,
    })

    useEffect(() => {
        const calculateTimeLeft = () => {
            const difference = targetDate.getTime() - new Date().getTime()
            if (difference > 0) {
                setTimeLeft({
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60),
                })
            }
        }

        calculateTimeLeft()
        const timer = setInterval(calculateTimeLeft, 1000)
        return () => clearInterval(timer)
    }, [targetDate])

    return timeLeft
}

// Copy Button Component
function CopyButton({ text }: { text: string }) {
    const [copied, setCopied] = useState(false)

    const handleCopy = () => {
        navigator.clipboard.writeText(text)
        setCopied(true)
        setTimeout(() => setCopied(false), 2000)
    }

    return (
        <button
            onClick={handleCopy}
            className="p-1 rounded hover:bg-muted transition-colors"
            title="Copy"
        >
            {copied ? (
                <CheckCircle2 className="h-3.5 w-3.5 text-primary" />
            ) : (
                <Copy className="h-3.5 w-3.5 text-muted-foreground" />
            )}
        </button>
    )
}

// Lock Status Badge
function LockStatusBadge({ status }: { status: string }) {
    const variants: Record<string, { variant: "default" | "secondary" | "destructive"; className: string }> = {
        locked: { variant: "default", className: "bg-primary/10 text-primary border-primary/20" },
        unlocking: { variant: "secondary", className: "bg-yellow-500/10 text-yellow-600 border-yellow-500/20" },
        unlocked: { variant: "secondary", className: "bg-[#7ed957]/10 text-[#6bc448] border-[#7ed957]/20" },
    }

    const config = variants[status] || variants.locked

    return (
        <Badge variant="outline" className={config.className}>
            {status === "locked" && <Lock className="h-3 w-3 mr-1" />}
            {status === "unlocking" && <Timer className="h-3 w-3 mr-1" />}
            {status === "unlocked" && <CheckCircle2 className="h-3 w-3 mr-1" />}
            {status.charAt(0).toUpperCase() + status.slice(1)}
        </Badge>
    )
}

// Lock Card Component
function LockCard({ lock }: { lock: any }) {
    const timeLeft = useCountdown(lock.unlockDate)
    const progress = Math.min(100, ((Date.now() - lock.lockDate.getTime()) / (lock.unlockDate.getTime() - lock.lockDate.getTime())) * 100)

    return (
        <Link href={`/lock/${lock.id}`}>
            <Card className="hover:border-primary/50 transition-all cursor-pointer group">
                <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                        {/* Token Info */}
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                                {lock.type === "lp" ? (
                                    <Droplets className="h-5 w-5 text-primary" />
                                ) : (
                                    <Coins className="h-5 w-5 text-primary" />
                                )}
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className="font-semibold group-hover:text-primary transition-colors">
                                        {lock.tokenName}
                                    </span>
                                    <Badge variant="outline" className="text-xs">
                                        {lock.tokenSymbol}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                    <code>{lock.tokenAddress.slice(0, 6)}...{lock.tokenAddress.slice(-4)}</code>
                                    <CopyButton text={lock.tokenAddress} />
                                </div>
                            </div>
                        </div>

                        {/* Status */}
                        <LockStatusBadge status={lock.status} />
                    </div>

                    {/* Lock Details */}
                    <div className="mt-4 grid grid-cols-2 gap-4">
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">Amount Locked</div>
                            <div className="font-mono font-medium text-sm">
                                {lock.amount.toLocaleString()} {lock.tokenSymbol}
                            </div>
                        </div>
                        <div>
                            <div className="text-xs text-muted-foreground mb-1">Value (USD)</div>
                            <div className="font-mono font-medium text-sm text-primary">
                                ${lock.valueUSD.toLocaleString()}
                            </div>
                        </div>
                    </div>

                    {/* Unlock Progress */}
                    <div className="mt-4 space-y-2">
                        <div className="flex items-center justify-between text-xs">
                            <span className="text-muted-foreground">Unlock Progress</span>
                            <span className="font-medium">
                                {lock.status === "unlocked" ? "Unlocked" : `${timeLeft.days}d ${timeLeft.hours}h remaining`}
                            </span>
                        </div>
                        <Progress value={progress} className="h-1.5" />
                    </div>

                    {/* Footer */}
                    <div className="mt-4 pt-3 border-t border-border/50 flex items-center justify-between text-xs text-muted-foreground">
                        <span>Unlock: {lock.unlockDate.toLocaleDateString()}</span>
                        <span className="flex items-center gap-1">
                            <Shield className="h-3 w-3" />
                            {lock.network}
                        </span>
                    </div>
                </CardContent>
            </Card>
        </Link>
    )
}

// Stats Card
function StatsCard({ icon: Icon, label, value, subtext }: { icon: any; label: string; value: string; subtext?: string }) {
    return (
        <Card>
            <CardContent className="p-4">
                <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                        <Icon className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                        <div className="text-xs text-muted-foreground">{label}</div>
                        <div className="font-bold text-lg">{value}</div>
                        {subtext && <div className="text-xs text-muted-foreground">{subtext}</div>}
                    </div>
                </div>
            </CardContent>
        </Card>
    )
}

// Helper to map lock data to display format
const mapLockData = (data: any[]) => {
    return data.map(lock => ({
        ...lock,
        tokenName: lock.token?.name || "Unknown",
        tokenSymbol: lock.token?.symbol || "???",
        tokenAddress: lock.token?.address || "0x000...000",
        unlockDate: new Date(lock.unlockDate),
        lockDate: new Date(lock.createdAt),
        valueUSD: 0, // Fallback for now
        status: lock.unlocked ? "unlocked" : (new Date(lock.unlockDate) <= new Date() ? "unlocking" : "locked"),
        type: lock.isLP ? "lp" : "token",
        network: lock.chainId === 97 ? "BSC Testnet" : (lock.chainId === 11155111 ? "Sepolia" : "Chain " + lock.chainId)
    }))
}

export function TokenLocker({ initialLocks = [] }: { initialLocks?: any[] }) {
    const [searchQuery, setSearchQuery] = useState("")
    const [selectedNetwork, setSelectedNetwork] = useState("all")
    const [selectedType, setSelectedType] = useState("all")
    // Initialize with mapped initialLocks if available
    const [locks, setLocks] = useState<any[]>(() => initialLocks.length > 0 ? mapLockData(initialLocks) : [])
    const [isLoading, setIsLoading] = useState(initialLocks.length === 0)
    const { isConnected, connectWallet, address } = useWallet()
    const chainId = useChainId()

    useEffect(() => {
        // If we have initial locks and haven't fetched yet, we might want to skip the first fetch 
        // OR we want to fetch to ensure we have the specific chain data if that matches the UI logic.
        // For now, we'll fetch to ensure consistency with wallet state, but the initial render will be populated.
        fetchLocks()
    }, [address, chainId])

    const fetchLocks = async () => {
        // Only show loading if we don't have locks (prevents flash when switching chains if we want)
        // or just keep it simple.
        if (locks.length === 0) setIsLoading(true)
        try {
            const url = new URL('/api/locks', window.location.origin)
            // Fetch all locks for the chain, we'll filter 'My Locks' locally or show all here
            url.searchParams.append('chainId', chainId.toString())

            const res = await fetch(url.toString())
            const data = await res.json()
            if (Array.isArray(data)) {
                setLocks(mapLockData(data))
            }
        } catch (error) {
            console.error('Error fetching locks:', error)
        } finally {
            setIsLoading(false)
        }
    }

    const filteredLocks = locks.filter((lock) => {
        const matchesSearch =
            lock.token?.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            lock.token?.symbol?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            lock.token?.address?.toLowerCase().includes(searchQuery.toLowerCase())
        const matchesType = selectedType === "all" || (selectedType === "lp" ? lock.isLP : !lock.isLP)
        return matchesSearch && matchesType
    })

    const totalLocks = locks.length
    const activeLocks = locks.filter((lock) => !lock.unlocked).length

    return (
        <div className="space-y-6">
            {/* Header with Create Button */}
            <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight">Token Locker</h2>
                    <p className="text-sm text-muted-foreground">Explore and manage locked tokens across multiple networks.</p>
                </div>
                <Link href="/create-lock">
                    <Button variant="stargate" className="gap-2 shadow-lg shadow-primary/20">
                        <Lock className="h-4 w-4" />
                        Create New Lock
                    </Button>
                </Link>
            </div>

            {/* Stats Overview */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatsCard
                    icon={Lock}
                    label="Active Locks"
                    value={activeLocks.toString()}
                />
                <StatsCard
                    icon={Coins}
                    label="Total Locks"
                    value={totalLocks.toString()}
                />
                <StatsCard
                    icon={TrendingUp}
                    label="Tokens"
                    value={locks.filter((l) => !l.isLP).length.toString()}
                />
                <StatsCard
                    icon={Droplets}
                    label="LP Tokens"
                    value={locks.filter((l) => l.isLP).length.toString()}
                />
            </div>

            {/* Tabs */}
            <Tabs defaultValue="all" className="w-full">
                <TabsList className="bg-muted/50 p-1 mb-6">
                    <TabsTrigger value="all" className="gap-2 rounded-md">
                        <Lock className="h-4 w-4" />
                        All Locks
                    </TabsTrigger>
                    <TabsTrigger value="my-locks" className="gap-2 rounded-md">
                        <Wallet className="h-4 w-4" />
                        My Locks
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="all" className="space-y-6">
                    {/* Search and Filters */}
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                            <Input
                                placeholder="Search by token name, symbol, or address..."
                                className="pl-10 h-11"
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <div className="flex gap-2">
                            <select
                                className="px-3 py-2 h-11 rounded-lg border bg-background text-sm"
                                value={selectedNetwork}
                                onChange={(e) => setSelectedNetwork(e.target.value)}
                            >
                                <option value="all">All Networks</option>
                                <option value="BSC">BSC</option>
                                <option value="Ethereum">Ethereum</option>
                                <option value="Polygon">Polygon</option>
                            </select>
                            <select
                                className="px-3 py-2 h-11 rounded-lg border bg-background text-sm"
                                value={selectedType}
                                onChange={(e) => setSelectedType(e.target.value)}
                            >
                                <option value="all">All Types</option>
                                <option value="token">Tokens</option>
                                <option value="lp">LP Tokens</option>
                            </select>
                        </div>
                    </div>

                    {/* Locks Grid */}
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-2 gap-4"
                        initial="hidden"
                        animate="visible"
                        variants={{
                            hidden: { opacity: 0 },
                            visible: { opacity: 1, transition: { staggerChildren: 0.08, delayChildren: 0.1 } }
                        }}
                    >
                        <AnimatePresence mode="popLayout">
                            {filteredLocks.map((lock) => (
                                <motion.div
                                    key={lock.id}
                                    layout
                                    initial={{ opacity: 0, y: 15 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, scale: 0.95 }}
                                    transition={{ duration: 0.3, ease: "easeOut" }}
                                >
                                    <LockCard lock={lock} />
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </motion.div>

                    {isLoading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-40 rounded-xl" />)}
                        </div>
                    ) : filteredLocks.length === 0 && (
                        <div className="text-center py-12 bg-muted/20 rounded-2xl border border-dashed">
                            <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4 opacity-20" />
                            <h3 className="text-lg font-semibold mb-2">No locks found</h3>
                            <p className="text-muted-foreground">Try adjusting your search or filters</p>
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="my-locks" className="space-y-6">
                    {!isConnected ? (
                        <Card className="py-12 border-dashed">
                            <CardContent className="text-center">
                                <Wallet className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                <h3 className="text-lg font-semibold mb-2">Connect Your Wallet</h3>
                                <p className="text-muted-foreground mb-4">
                                    Connect your wallet to view your locked tokens
                                </p>
                                <Button onClick={connectWallet} className="gap-2">
                                    <Wallet className="h-4 w-4" />
                                    Connect Wallet
                                </Button>
                            </CardContent>
                        </Card>
                    ) : locks.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {locks.filter(l => l.owner.walletAddress === address?.toLowerCase()).map((lock) => (
                                <LockCard key={lock.id} lock={lock} />
                            ))}
                        </div>
                    ) : (
                        <Card className="py-12 border-dashed">
                            <CardContent className="text-center">
                                <Lock className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                                <h3 className="text-lg font-semibold mb-2">No Locks Found</h3>
                                <p className="text-muted-foreground mb-4">
                                    You haven't created any token locks yet
                                </p>
                                <Button className="gap-2" asChild>
                                    <Link href="/create-lock">
                                        <Lock className="h-4 w-4" />
                                        Create Your First Lock
                                    </Link>
                                </Button>
                            </CardContent>
                        </Card>
                    )}
                </TabsContent>
            </Tabs>
        </div>
    )
}

export default TokenLocker
